const REFERENTIAL_ADMIN_PERMISSIONS = [
  'REFERENTIAL_DOMAIN_VIEW',
  'REFERENTIAL_DOMAIN_CREATE',
  'REFERENTIAL_DOMAIN_UPDATE',
  'REFERENTIAL_DOMAIN_DELETE',
  'REFERENTIAL_CATEGORY_VIEW',
  'REFERENTIAL_CATEGORY_CREATE',
  'REFERENTIAL_CATEGORY_UPDATE',
  'REFERENTIAL_CATEGORY_DELETE',
  'REFERENTIAL_ITEM_VIEW',
  'REFERENTIAL_ITEM_CREATE',
  'REFERENTIAL_ITEM_UPDATE',
  'REFERENTIAL_ITEM_DELETE',
  'REFERENTIAL_ITEM_ACTIVATE',
  'REFERENTIAL_ITEM_DEACTIVATE',
  'REFERENTIAL_IMPORT_PREVIEW',
  'REFERENTIAL_IMPORT_EXECUTE',
  'REFERENTIAL_EXPORT',
  'REFERENTIAL_USAGE_VIEW',
  'REFERENTIAL_USAGE_REGISTER',
  'REFERENTIAL_LOOKUP_READ',
  'referential.admin.manage',
] as const;

const GESTION_ACCESS_ADMIN_PERMISSIONS = [
  'GESTION_ACCESS_VIEW',
  'GESTION_ACCESS_CREATE',
  'GESTION_ACCESS_UPDATE_OWN',
  'GESTION_ACCESS_VALIDATE_ZONE',
  'GESTION_ACCESS_VALIDATE_SECURITY',
  'GESTION_ACCESS_VALIDATE_PAYMENT',
  'GESTION_ACCESS_VALIDATE_DGSN',
  'GESTION_ACCESS_ADMIN',
  'GESTION_ACCESS_AUDIT_VIEW',
  'GESTION_ACCESS_DOCUMENT_UPLOAD',
] as const;

const DUM_ADMIN_PERMISSIONS = [
  'dum.view',
  'dum.manage',
  'dum.admin',
] as const;

const INTEGRATION_ADMIN_PERMISSIONS = [
  'flow.access',
  'flow.operations.*',
  'flow.analytics.*',
  'integration.execution.*',
  'integration.dlq.*',
  'integration.replay.*',
  'integration.partner.view',
  'integration.partner.create',
  'integration.partner.edit',
  'integration.partner.delete',
  'integration.partner.submit',
  'integration.partner.approve',
  'integration.partner.reject',
  'integration.partner.suspend',
  'integration.partner.reactivate',
  'integration.partner.rollback',
  'integration.partner.connectivity.test',
  'integration.partner.audit.view',
  'integration.secret.create',
  'integration.secret.validate',
  'integration.secret.metadata.view',
  'integration.secret.rotate',
] as const;

const ENS_ADMIN_PERMISSIONS = [
  'ens.filing.create',
  'ens.filing.read.all',
  'ens.filing.update.draft',
  'ens.filing.delete.draft',
  'ens.filing.submit',
  'ens.filing.copy',
  'ens.filing.amend',
  'ens.filing.print',
  'ens.filing.export',
  'ens.exchange.read',
  'ens.exchange.read.raw',
  'ens.exchange.replay',
  'ens.reconciliation.read',
  'ens.reconciliation.manage',
  'ens.reconciliation.correlate',
  'ens.profile.read',
  'ens.profile.manage',
  'ens.audit.read',
] as const;

const PLATFORM_ADMIN_PERMISSIONS = [
  'OWNER_ACCESS',
  'DASHBOARD_VIEW',
  'IDENTITIES_VIEW',
  'IDENTITIES_MANAGE',
  'ORGANIZATIONS_VIEW',
  'ORGANIZATIONS_MANAGE',
  'ORGANIZATIONS_UPDATE',
  'ORGANIZATIONS_TRANSFER_OWNERSHIP',
  'AUTHORIZATION_VIEW',
  'AUTHORIZATION_MANAGE',
  'AUDIT_VIEW',
  ...REFERENTIAL_ADMIN_PERMISSIONS,
  ...GESTION_ACCESS_ADMIN_PERMISSIONS,
  ...DUM_ADMIN_PERMISSIONS,
  ...INTEGRATION_ADMIN_PERMISSIONS,
  ...ENS_ADMIN_PERMISSIONS,
] as const;

const PLATFORM_OPERATOR_PERMISSIONS = [
  'DASHBOARD_VIEW',
  'IDENTITIES_VIEW',
  'IDENTITIES_MANAGE',
  'ORGANIZATIONS_VIEW',
  'ORGANIZATIONS_MANAGE',
  'AUTHORIZATION_VIEW',
  'AUTHORIZATION_MANAGE',
] as const;

const PLATFORM_AUDITOR_PERMISSIONS = [
  'DASHBOARD_VIEW',
  'AUDIT_VIEW',
] as const;

const FLOW_ADMIN_PERMISSIONS = [
  ...INTEGRATION_ADMIN_PERMISSIONS,
  'SCANNER_MONITOR_VIEW',
  'SCANNER_MONITOR_VIEW_XML',
  'SCANNER_MONITOR_VIEW_IMAGES',
  'SCANNER_MONITOR_VIEW_ERRORS',
  'SCANNER_MONITOR_RETRY',
  'SCANNER_MONITOR_EXPORT',
] as const;

export function resolvePermissionsForRoles(roles: readonly string[]): string[] {
  const normalizedRoles = new Set(roles.map((role) => role.trim().toLowerCase()));
  const permissions = new Set<string>();
  const rolePermissions = new Map<string, readonly string[]>([
    ['platform-admin', PLATFORM_ADMIN_PERMISSIONS],
    ['platform-operator', PLATFORM_OPERATOR_PERMISSIONS],
    ['auditor', PLATFORM_AUDITOR_PERMISSIONS],
    ['flow-admin', FLOW_ADMIN_PERMISSIONS],
    ['oss.flow.admin', FLOW_ADMIN_PERMISSIONS],
    ['flow-viewer', ['flow.access']],
    ['flow-operator', ['flow.access', 'flow.operations.execute']],
    ['flow-configurator', ['flow.access', 'flow.operations.execute']],
    ['flow-reviewer', ['flow.access', 'flow.analytics.read']],
    ['referential-admin', REFERENTIAL_ADMIN_PERMISSIONS],
    ['referential_admin', REFERENTIAL_ADMIN_PERMISSIONS],
    ['access_admin', GESTION_ACCESS_ADMIN_PERMISSIONS],
    ['access-admin', GESTION_ACCESS_ADMIN_PERMISSIONS],
    ['gestion-access.admin', GESTION_ACCESS_ADMIN_PERMISSIONS],
    ['dum-viewer', ['dum.view']],
    ['dum-operator', ['dum.view', 'dum.manage']],
    ['dum-manager', ['dum.view', 'dum.manage']],
    ['dum-admin', DUM_ADMIN_PERMISSIONS],
  ]);

  for (const role of normalizedRoles) {
    const grants = rolePermissions.get(role);
    if (!grants) {
      continue;
    }

    for (const permission of grants) {
      permissions.add(permission);
    }
  }

  return [...permissions];
}
